import { supabase } from '@/lib/supabase';
import { 
  ClientProfile, 
  ClientFormData,
  DiagnosisResult,
  DiagnosisFormData,
  ConsultationBooking,
  BookingFormData,
  ConversionFunnelStats
} from '@/types/models';
import { Database } from '@/types/database.types';

type ClientProfileRow = Database['public']['Tables']['client_profiles']['Row'];
type ClientProfileInsert = Database['public']['Tables']['client_profiles']['Insert'];
type DiagnosisResultRow = Database['public']['Tables']['diagnosis_results']['Row'];
type DiagnosisResultInsert = Database['public']['Tables']['diagnosis_results']['Insert'];
type ConsultationBookingRow = Database['public']['Tables']['consultation_bookings']['Row'];
type ConsultationBookingInsert = Database['public']['Tables']['consultation_bookings']['Insert'];

// Conversion helpers
const convertClientProfileToModel = (row: ClientProfileRow): ClientProfile => ({
  id: row.id,
  email: row.email,
  fullName: row.full_name,
  phone: row.phone,
  source: row.source,
  utmSource: row.utm_source,
  utmMedium: row.utm_medium,
  utmCampaign: row.utm_campaign,
  firstInteractionDate: row.first_interaction_date,
  lastInteractionDate: row.last_interaction_date,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const convertDiagnosisResultToModel = (row: DiagnosisResultRow): DiagnosisResult => ({
  id: row.id,
  clientId: row.client_id,
  diagnosisStatus: row.diagnosis_status,
  completionTime: row.completion_time,
  answers: row.answers,
  gaSessionId: row.ga_session_id,
  gaClientId: row.ga_client_id,
  startedAt: row.started_at,
  completedAt: row.completed_at,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const convertConsultationBookingToModel = (row: ConsultationBookingRow): ConsultationBooking => ({
  id: row.id,
  clientId: row.client_id,
  diagnosisId: row.diagnosis_id,
  bookingStatus: row.booking_status,
  preferredDate: row.preferred_date,
  preferredTime: row.preferred_time,
  meetingType: row.meeting_type,
  notes: row.notes,
  gaSessionId: row.ga_session_id,
  gaClientId: row.ga_client_id,
  requestedAt: row.requested_at,
  confirmedAt: row.confirmed_at,
  completedAt: row.completed_at,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

export const clientService = {
  // ============================================================================
  // NEW: Email-Based Client Upsert (Single Source of Truth)
  // ============================================================================
  
  async upsertClientByEmail(data: {
    email: string;
    fullName?: string;
    phone?: string;
    source?: 'organic' | 'social' | 'referral' | 'direct' | 'campaign';
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
  }): Promise<{ data: string | null; error: Error | null }> {
    try {
      const { data: clientId, error } = await supabase.rpc('upsert_client_by_email', {
        p_email: data.email,
        p_full_name: data.fullName || null,
        p_phone: data.phone || null,
        p_source: data.source || 'organic',
        p_utm_source: data.utmSource || null,
        p_utm_medium: data.utmMedium || null,
        p_utm_campaign: data.utmCampaign || null,
      });

      if (error) throw error;
      return { data: clientId, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Client Profile operations
  async createClient(data: ClientFormData): Promise<{ data: ClientProfile | null; error: Error | null }> {
    try {
      const insertData: ClientProfileInsert = {
        email: data.email,
        full_name: data.fullName,
        phone: data.phone,
        source: data.source,
        utm_source: data.utmSource,
        utm_medium: data.utmMedium,
        utm_campaign: data.utmCampaign,
      };

      const { data: result, error } = await supabase
        .from('client_profiles')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return { data: result ? convertClientProfileToModel(result) : null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  async getClientByEmail(email: string): Promise<{ data: ClientProfile | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('client_profiles')
        .select('*')
        .eq('email', email)
        .single();

      if (error) throw error;
      return { data: data ? convertClientProfileToModel(data) : null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  async getAllClients(): Promise<{ data: ClientProfile[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('client_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { 
        data: data ? data.map(convertClientProfileToModel) : null, 
        error: null 
      };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // ============================================================================
  // UPDATED: Diagnosis Results Operations with Complete Answers
  // ============================================================================
  
  async createDiagnosisResult(data: DiagnosisFormData & {
    // Add contact data to ensure it's in answers from the start
    name?: string;
    email?: string;
    phone?: string;
  }): Promise<{ data: DiagnosisResult | null; error: Error | null }> {
    try {
      // Merge all form data into answers including contact data
      const completeAnswers = {
        ...data.answers,
        name: data.name,
        email: data.email,
        phone: data.phone,
      };

      const insertData: DiagnosisResultInsert = {
        client_id: data.clientId,
        answers: completeAnswers,
        ga_session_id: data.gaSessionId,
        ga_client_id: data.gaClientId,
        diagnosis_status: 'in_progress',
      };

      const { data: result, error } = await supabase
        .from('diagnosis_results')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return { data: result ? convertDiagnosisResultToModel(result) : null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // ============================================================================
  // UPDATED: Complete Diagnosis with Full Answers
  // ============================================================================
  
  async completeDiagnosisResult(
    diagnosisId: string,
    completionTime: number,
    finalAnswers: any // Complete answers including all diagnosis steps + contact data
  ): Promise<{ data: DiagnosisResult | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('diagnosis_results')
        .update({
          diagnosis_status: 'completed',
          completion_time: completionTime,
          completed_at: new Date().toISOString(),
          answers: finalAnswers, // Update with complete answers including contact data
        })
        .eq('id', diagnosisId)
        .select()
        .single();

      if (error) throw error;
      return { data: data ? convertDiagnosisResultToModel(data) : null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  async getDiagnosisResultsByClient(clientId: string): Promise<{ data: DiagnosisResult[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('diagnosis_results')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { 
        data: data ? data.map(convertDiagnosisResultToModel) : null, 
        error: null 
      };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get diagnosis results for a specific client
   */
  async getClientDiagnosis(clientId: string) {
    const { data, error } = await supabase
      .from('diagnosis_results')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });

    return { data, error };
  },

  // Consultation Booking operations
  async createConsultationBooking(data: BookingFormData): Promise<{ data: ConsultationBooking | null; error: Error | null }> {
    try {
      const insertData: ConsultationBookingInsert = {
        client_id: data.clientId,
        diagnosis_id: data.diagnosisId,
        preferred_date: data.preferredDate,
        preferred_time: data.preferredTime,
        meeting_type: data.meetingType,
        notes: data.notes,
        ga_session_id: data.gaSessionId,
        ga_client_id: data.gaClientId,
      };

      const { data: result, error } = await supabase
        .from('consultation_bookings')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return { data: result ? convertConsultationBookingToModel(result) : null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  async updateBookingStatus(
    bookingId: string,
    status: 'requested' | 'confirmed' | 'completed' | 'cancelled'
  ): Promise<{ data: ConsultationBooking | null; error: Error | null }> {
    try {
      const updateData: any = {
        booking_status: status,
        updated_at: new Date().toISOString(),
      };

      if (status === 'confirmed') {
        updateData.confirmed_at = new Date().toISOString();
      } else if (status === 'completed') {
        updateData.completed_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('consultation_bookings')
        .update(updateData)
        .eq('id', bookingId)
        .select()
        .single();

      if (error) throw error;
      return { data: data ? convertConsultationBookingToModel(data) : null, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get all bookings with client info and slot info
  async getAllBookings(): Promise<{ data: ConsultationBooking[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('consultation_bookings')
        .select(
          `
          *,
          client:client_profiles(*),
          slot:availability_slots(*)
        `
        )
        .order('requested_at', { ascending: false });

      if (error) throw error;
      
      return {
        data: data?.map((row: any) => ({
          id: row.id,
          clientId: row.client_id,
          slotId: row.slot_id,
          meetingType: row.meeting_type,
          bookingStatus: row.booking_status,
          notes: row.notes,
          location: row.location, // Include location
          preferredDate: row.preferred_date,
          preferredTime: row.preferred_time,
          requestedAt: row.requested_at,
          confirmedAt: row.confirmed_at,
          completedAt: row.completed_at,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
          client: row.client ? convertClientProfileToModel(row.client) : null,
          slot: row.slot ? { // Map slot data
            id: row.slot.id,
            slotDate: row.slot.slot_date,
            slotTime: row.slot.slot_time,
            meetingType: row.slot.meeting_type,
            isAvailable: row.slot.is_available,
            maxBookings: row.slot.max_bookings,
            currentBookings: row.slot.current_bookings,
            durationMinutes: row.slot.duration_minutes,
            createdAt: row.slot.created_at,
            updatedAt: row.slot.updated_at,
          } : null,
        })) ?? null,
        error: null,
      };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Analytics operations
  async getConversionFunnelStats(
    startDate?: string,
    endDate?: string
  ): Promise<{ data: ConversionFunnelStats | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.rpc('get_conversion_funnel_stats', {
        start_date: startDate,
        end_date: endDate,
      });

      if (error) throw error;
      
      const stats = data?.[0];
      if (!stats) {
        return { data: null, error: new Error('No stats found') };
      }

      const result: ConversionFunnelStats = {
        totalVisitors: stats.total_visitors,
        diagnosisStarted: stats.diagnosis_started,
        diagnosisCompleted: stats.diagnosis_completed,
        bookingsRequested: stats.bookings_requested,
        bookingsConfirmed: stats.bookings_confirmed,
        diagnosisCompletionRate: stats.diagnosis_completion_rate,
        bookingConversionRate: stats.booking_conversion_rate,
      };

      return { data: result, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};